import React, { useEffect, useMemo, useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { IconPlus } from "@tabler/icons-react";
import { Loader } from "../../components/animation/Loader";
import DataTable from "../../components/table/DataTable";
import { get_countries } from "../../service/api/locations";
import { get_compliances } from "../../service/api/compliance";
import { get_services } from "../../service/api/service";
import type { ICompliance } from "../../store/@types/compliance";
import { useAppDispatch, useAppSelector } from "../../store";
import { setTitle } from "../../store/slices/navbar";
import CreateComplianceModal from "./create";

const ComplianceDashboard: React.FC = () => {
  const dispatch = useAppDispatch();
  const compliances = useAppSelector(
    ({ compliance }) => compliance.compliances,
  );
  const loading = useAppSelector(({ compliance }) => compliance.loading);
  const countries = useAppSelector(({ locations }) => locations.countries);
  const services = useAppSelector(({ service }) => service.services);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    dispatch(setTitle("Compliance"));
    void get_compliances(dispatch);
  }, [dispatch]);

  useEffect(() => {
    if (countries.length === 0) {
      void get_countries(dispatch);
    }
  }, [countries.length, dispatch]);

  useEffect(() => {
    if (services.length === 0) {
      void get_services(dispatch, 100, 0);
    }
  }, [dispatch, services.length]);

  const countryNamesById = useMemo(
    () =>
      new Map(countries.map((country) => [String(country.id), country.name])),
    [countries],
  );
  const serviceNamesById = useMemo(
    () =>
      new Map(services.map((service) => [String(service.id), service.name])),
    [services],
  );

  const columns = useMemo<ColumnDef<ICompliance>[]>(
    () => [
      {
        accessorKey: "application_number",
        header: "Application No.",
      },
      {
        accessorKey: "company_name",
        header: "Company",
      },
      {
        accessorKey: "selected_country",
        header: "Country",
        cell: ({ row }) =>
          countryNamesById.get(String(row.original.selected_country)) ??
          row.original.selected_country,
      },
      {
        accessorKey: "selected_services",
        header: "Services",
        cell: ({ row }) =>
          row.original.selected_services
            .map(
              (serviceId) =>
                serviceNamesById.get(String(serviceId)) ?? serviceId,
            )
            .join(", ") || "—",
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => (
          <span className="rounded-full bg-primary-50/10 px-3 py-1 text-xs font-medium capitalize text-primary-700">
            {row.original.status}
          </span>
        ),
      },
      {
        accessorKey: "submitted_at",
        header: "Submitted",
        cell: ({ row }) => {
          const submittedAt = new Date(row.original.submitted_at);
          return Number.isNaN(submittedAt.getTime())
            ? "—"
            : submittedAt.toLocaleDateString();
        },
      },
    ],
    [countryNamesById, serviceNamesById],
  );

  if (loading && compliances.length === 0) {
    return <Loader />;
  }

  return (
    <main className="w-full space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Compliance</h1>
          <p className="mt-1 text-sm text-gray-500">
            View and manage your compliance requests.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          className="inline-flex items-center gap-2 rounded-lg bg-primary-500 px-4 py-2.5 font-medium text-white transition-colors hover:bg-primary-700"
        >
          <IconPlus size={18} />
          Create compliance request
        </button>
      </div>

      <DataTable columns={columns} data={compliances} hasPaginate={false} />

      <CreateComplianceModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </main>
  );
};

export default ComplianceDashboard;
